import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type FocusEvent,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { setTentativeETALocation } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import type { SetFieldValue } from '../../types';
import { Label } from '../AirAmbulanceFormStandalone.styles';
import { PICKUP_LOCATION } from '../constants';
import {
  SuggestionsContainer,
  SuggestionsList,
} from './AddressSearchInput.styled';
import { SuggestedAddress, type GeocodedAddress } from './SuggestedAddress';

interface AddressSearchInputProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: SetFieldValue;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  error?: string;
  geocodedResults?: GeocodedAddress[];
  onSelectSuggestion: (suggestion: GeocodedAddress) => void;
  onAddressChange: (e: ChangeEvent<HTMLInputElement>) => void;
  requestId?: string;
}

const AddressSearchInput = ({
  value,
  onChange,
  setFieldValue,
  onBlur,
  error,
  geocodedResults,
  onSelectSuggestion,
  onAddressChange,
}: AddressSearchInputProps): JSX.Element => {
  const dispatch = useDispatch();
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Handle selecting a suggestion
  const handleSelectSuggestion = (suggestion: GeocodedAddress): void => {
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    const location = suggestion?.location;
    if (location?.y !== undefined && location?.x !== undefined) {
      dispatch(
        setTentativeETALocation({
          latitude: location.y,
          longitude: location.x,
        }),
      );
    }
  };

  // Handle address input change
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(e);
    onAddressChange(e);

    if (e.target.value) {
      setShowSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle click outside suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside as EventListener);
    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside as EventListener,
      );
      dispatch(setTentativeETALocation(null));
    };
  }, []); //eslint-disable-line

  return (
    <Box pb={3}>
      <Label>
        <Text size="small" color="primaryText">
          Pickup Location*
        </Text>
      </Label>
      <SuggestionsContainer ref={suggestionsRef}>
        <InputText
          id={PICKUP_LOCATION}
          name={PICKUP_LOCATION}
          shouldErrorTakeSpace
          type="text"
          setFieldValue={setFieldValue}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          error={error}
          style={{ fontSize: '14px' }}
        />
        {showSuggestions && geocodedResults && geocodedResults.length > 0 && (
          <SuggestionsList>
            {geocodedResults.map((result, index) => (
              <SuggestedAddress
                key={index}
                isSelected={index === selectedSuggestionIndex}
                index={index}
                address={result}
                handleSelectSuggestion={handleSelectSuggestion}
                setSelectedSuggestionIndex={indexOrEvent => {
                  // TODO: REVIEW: This looks off to me. It looks like indexOrEvent is always an event, but we only ever treated it like it was an index.
                  if (typeof indexOrEvent === 'number') {
                    setSelectedSuggestionIndex(indexOrEvent);
                  }
                }}
              />
            ))}
          </SuggestionsList>
        )}
      </SuggestionsContainer>
    </Box>
  );
};

export default AddressSearchInput;
